import React, { type LegacyRef, useRef } from "react";
import { type VideoTypes, VideoPlayer } from "@bloomberg/media-ui-av";

import useVideoViewportAutoplay from "~components/shared/video-player/useVideoViewportAutoplay";
import type { LineupVideoPlayerProps } from "~components/shared/video-player/VideoPlayerProvider";
import { LINEUP_STREAM_IDS } from "~constants/curation";
import isEnabled from "~utils/features";

import { useVideoPlayer } from "./useVideoPlayer";

export interface LineupVideoLivePlayerProps extends LineupVideoPlayerProps {
    livestream?: string;
}

const isVideoViewportAutoplay = isEnabled("videoViewportAutoplay");

export const LineupVideoLivePlayer = React.forwardRef<
    HTMLElement,
    LineupVideoLivePlayerProps
>(function LiveStreamPlayerComponent(
    props: LineupVideoLivePlayerProps,
    ref: React.ForwardedRef<HTMLElement>
) {
    const playerRef = useRef<VideoTypes.MediaPlayer>(null);
    let intersectionObserverTracker = null;
    let onReady = null;
    if (isVideoViewportAutoplay) {
        ({ intersectionObserverTracker, onReady } =
            // the value isVideoViewportAutoplay is set on build time, so it is constant
            // eslint-disable-next-line react-hooks/rules-of-hooks
            useVideoViewportAutoplay(playerRef));
    }

    const { livestream, onError } = props;
    const {
        env,
        autoplay,
        options,
        foundationLayout,
        preloadThreshold,
        repeat,
        startFrom,
        startMuted,
        controls,
    } = useVideoPlayer();

    const liveOptions = {
        ...options,
        id: livestream ?? LINEUP_STREAM_IDS.US,
        live: true,
        controls,
    };

    return (
        <div
            data-component="live-stream-player"
            ref={intersectionObserverTracker as LegacyRef<HTMLDivElement>}
        >
            <VideoPlayer
                autoplay={autoplay}
                env={env}
                foundationLayout={foundationLayout}
                onError={onError}
                onReady={onReady ?? (() => 0)}
                options={liveOptions}
                playerRef={playerRef}
                preloadThreshold={preloadThreshold}
                ref={ref}
                repeat={repeat}
                startFrom={startFrom}
                startMuted={startMuted}
            />
        </div>
    );
});
